'use client';

import Image from 'next/image';
import type { ReactNode } from 'react';
import { Link } from '../../../components/ui/Link';
import { ROUTES } from '../../../constants/routes';
import { themeConfig } from '../../../theme';

/** Login / reset-password link accent (matches sign-in “Forgot password?”). */
export const AUTH_LOGIN_LINK_CLASS =
  'h-auto min-h-0 p-0 text-sm font-medium text-[#2366bd] underline decoration-[#2366bd]/35 underline-offset-2 hover:decoration-[#2366bd]';

/** Eyebrow / accent label on the form column. */
export const AUTH_LOGIN_ACCENT_CLASS = 'text-sm font-medium text-[#800040]';

/** Status banner — same tokens as login success banners (`AuthPage`). */
export const AUTH_LOGIN_STATUS_BANNER_CLASS =
  'rounded-lg border border-primary-600/30 bg-primary-50 px-4 py-3 text-sm font-medium text-primary-900';

const AUTH_ASIDE_PANEL_CLASS =
  'relative order-2 flex min-h-[min(22rem,50vh)] flex-col justify-between rounded-2xl bg-[#800040] px-8 py-12 text-white lg:order-none lg:min-h-full lg:px-10 lg:py-14';

export type AuthSplitLayoutProps = {
  children: ReactNode;
  asideTitle: string;
  asideDescription: ReactNode;
  /** When false, omits the dashboard preview image (e.g. shorter aside copy only). */
  showPreviewImage?: boolean;
};

/** Two-column login shell: white form column + burgundy product aside. */
export function AuthSplitLayout({
  children,
  asideTitle,
  asideDescription,
  showPreviewImage = true,
}: AuthSplitLayoutProps) {
  return (
    <div className='flex flex-1 flex-col justify-center px-4 py-10 sm:px-6 lg:py-12'>
      <div className='mx-auto w-full max-w-6xl'>
        <div className='flex min-h-[min(36rem,calc(100vh-5rem))] flex-col bg-background lg:grid lg:min-h-[min(40rem,calc(100vh-6rem))] lg:grid-cols-2'>
          <section className='relative order-1 flex flex-1 flex-col bg-card px-6 py-10 sm:px-10 lg:order-none lg:justify-start lg:px-14 xl:px-16'>
            <Link
              href={ROUTES.home}
              className='fixed left-6 top-6 z-10 inline-block w-[182px] max-w-full focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#800040]'
            >
              <Image
                src='/brand/friendr-logo.svg'
                alt={themeConfig.brand.legalName}
                width={182}
                height={81}
                className='h-auto w-[182px] max-w-full'
                priority
              />
            </Link>
            {children}
          </section>

          <aside
            className={AUTH_ASIDE_PANEL_CLASS}
            aria-label='Product preview'
          >
            <div className='max-w-md'>
              <h2 className='font-heading text-2xl font-semibold leading-tight sm:text-3xl'>
                {asideTitle}
              </h2>
              <p className='mt-4 text-sm leading-relaxed text-white/90 sm:text-base'>
                {asideDescription}
              </p>
            </div>
            {showPreviewImage ? (
              <div className='relative mt-8 lg:mt-10'>
                <Image
                  src='/brand/auth-dashboard-preview.png'
                  alt='Dashboard preview — shelter workspace'
                  width={960}
                  height={540}
                  className='h-auto w-full rounded-xl border border-white/15 shadow-2xl shadow-black/25'
                  sizes='(max-width: 1024px) 100vw, 50vw'
                  priority
                />
              </div>
            ) : null}
          </aside>
        </div>
      </div>
    </div>
  );
}
